/**
 * composables/useComposerUpload.ts —— Composer 附件栏状态机（U-1d；102 `useComposerUpload.js` 逐条译 React+TS）。
 * 依据：设计件 §2.4 去向表（T29 可复用资产「上传状态机」按去向表迁移）／§7.3(a) 附件顺序／
 *   §11.1 三通道（chat-upload ∨ upload-large 双路由；P3 归属门＝后端校验，前端必带 session_id）；
 *   升级方案 §2 批 U-1d 行（有序草稿附件栏＋全视口拖放＋B-21 三坑）。
 * 纪律：请求一律经**注入面**（`uploadApi`／`pollApi`——本树出网只经 `src/transport/`（CONTRACT §1），
 *   上传端点归宿主接线位；缺省＝**显式失败**，禁静默假成功）；本地增删排序纯函数在
 *   `assembly/input/attachments.ts`（本层只做状态壳＋副作用）。
 *
 * B-21 三坑的落点（对照 102 `tests/composerUpload.test.js` T2/T3/T4 同语义）：
 *   ① submitting 守卫：提交在途 ⇒ 重复提交**整体忽略**（旧面无重入守卫的教训）；
 *   ② 等待态延迟：`waitDelayMs`（250）**之后**才把条目放进 `waitIds`（快速完成不闪 spinner；
 *      定时器句柄可注入 ⇒ 单测可用假时钟断言「不发等待态」）；
 *   ③ 会话归属：sessionId 在**提交时刻**取值并贯穿全程（不中途改读）；会话指针变化 ⇒
 *      在途 abort＋栏清空＋显式提示（旧面「切换即清空丢文件／发送中切会话传进新会话」双坑的对治）；
 *      自建会话避让＝`prevId === ''` 且指针未离开本次归属 ⇒ 不算切会话（A 方案同款）。
 *   ④ **A 方案（20260920，承大王令）**：提交时刻**无会话** ⇒ 先经注入的 `ensureSessionId` 建会话再传
 *      （归属语义不变，仍带 `sessionId`）；未注入 `ensureSessionId` 或建不出会话 ⇒
 *      维持 `blocked:'no-session'`（**零隐式建会话**、不静默）。
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  addFiles as addFilesPure,
  doneMetas,
  errorItems,
  metaOf,
  moveItem,
  pendingItems,
  removeItem,
  type AttachmentFileLike,
  type AttachmentItem,
  type AttachmentMeta,
} from "../assembly/input/attachments";

/** B-21②：等待态显示门槛（ms）——旧面无门槛（置 uploading 即出进度条），升级方案判据③补上 */
export const UPLOAD_WAIT_DELAY_MS = 250;

// 缺省依赖＝**模块级常量**（身份稳定）：若写成解构默认箭头，每次渲染都是新函数 ⇒
// `dispose`/`clearTimers` 身份随渲染漂移 ⇒ 卸载清理效应每渲染触发一次（误置 disposed）。
const defaultSchedule = (fn: () => void, ms: number): unknown => setTimeout(fn, ms);
const defaultUnschedule = (h: unknown): void => clearTimeout(h as ReturnType<typeof setTimeout>);
const defaultSleep = (ms: number): Promise<void> => new Promise<void>((r) => setTimeout(r, ms));
const defaultNotice = (): void => {};
/** FE-N-1 空错误回调敞口对治：缺省＝**显式报错**（console 留痕，禁静默吞错）。
 *  上层兜底＝宿主注入 onError 走人面提示（实证 `shell/Composer.tsx:173`）；未注入时错误不得无痕消失。 */
const defaultOnError = (text: string): void => {
  console.error("[useComposerUpload] 附件上传失败：", text);
};

/** 行内提示文案类别（单源呈现＝Composer `inlineNotice`，role=alert） */
export type ComposerNoticeKind = "info" | "warning" | "error";

/** 上传回执（102 `verbs.upload` 同形：`{ok:true,data:{fileId,channel}}`／失败 `{ok:false,errorText|errorCode}`） */
export type UploadApiResult =
  | { ok: true; data?: { fileId?: string; channel?: string } }
  | { ok: false; errorText?: string; errorCode?: string };

export type UploadApi = (
  file: AttachmentFileLike,
  opts: { sessionId: string; signal: AbortSignal },
) => Promise<UploadApiResult> | UploadApiResult;

/** 大文件分节轮询回执（102 `verbs.fileStatus` 同形） */
export type PollApiResult =
  | { ok: true; data?: { status?: string; sections?: number; finished_sections?: number } }
  | { ok: false };

export type PollApi = (args: { fileId: string; signal?: AbortSignal }) => Promise<PollApiResult> | PollApiResult;

export interface UseComposerUploadOptions {
  /** 会话身份读取器（附件归属锚——提交时刻取值／切换即清理，B-21③）；缺省读 `sessionId` */
  getSessionId?: () => string;
  /** 会话指针（**响应式信号**：变化即触发 B-21③ 切会话清理） */
  sessionId?: string;
  /** A 方案：无会话提交 ⇒ 先建会话（宿主注入；未注入 ⇒ `blocked:'no-session'`） */
  ensureSessionId?: (() => Promise<string> | string) | null;
  uploadApi?: UploadApi | null;
  pollApi?: PollApi | null;
  waitDelayMs?: number;
  schedule?: (fn: () => void, ms: number) => unknown;
  unschedule?: (handle: unknown) => void;
  sleep?: (ms: number) => Promise<void>;
  notice?: (text: string, kind?: ComposerNoticeKind) => void;
  onError?: (text: string) => void;
  mintId?: () => string;
  pollMaxRounds?: number;
}

/** 提交编排回执（与 102 同形：`{ignored|blocked|ok, metas, sessionId}`） */
export type SubmitUploadsResult =
  | { ignored: true }
  | { blocked: string }
  | { ok: true; metas: readonly AttachmentMeta[]; sessionId: string };

export interface UseComposerUpload {
  items: readonly AttachmentItem[];
  submitting: boolean;
  waitIds: readonly string[];
  uploadWaitVisible: boolean;
  hasPending: boolean;
  hasError: boolean;
  barEmpty: boolean;
  addFiles: (fileList: ReadonlyArray<AttachmentFileLike>) => void;
  removeAttachment: (id: string, opts?: { abortInflight?: boolean }) => void;
  moveAttachment: (id: string, delta: number) => void;
  retryUpload: (id: string) => void;
  submitUploads: () => Promise<SubmitUploadsResult>;
  notice: (text: string, kind?: ComposerNoticeKind) => void;
  abortAll: () => void;
  dispose: () => void;
}

export function useComposerUpload(opts: UseComposerUploadOptions = {}): UseComposerUpload {
  const {
    ensureSessionId = null,
    uploadApi = null,
    pollApi = null,
    waitDelayMs = UPLOAD_WAIT_DELAY_MS,
    schedule = defaultSchedule,
    unschedule = defaultUnschedule,
    sleep = defaultSleep,
    notice = defaultNotice,
    onError = defaultOnError,
    mintId,
    pollMaxRounds = 30,
  } = opts;

  // ── 响应式壳：逻辑读写走 ref（同步语义与 102 一致），state 只作渲染镜像 ──────────────
  const [items, setItemsState] = useState<readonly AttachmentItem[]>(Object.freeze([]));
  const [submitting, setSubmittingState] = useState(false);
  const [waitIds, setWaitIdsState] = useState<readonly string[]>(Object.freeze([]));
  const itemsRef = useRef<readonly AttachmentItem[]>(items);
  const waitIdsRef = useRef<readonly string[]>(waitIds);
  const submittingRef = useRef(false); // B-21①（同步守卫：同一 tick 内重入即忽略）
  const controllersRef = useRef(new Map<string, AbortController>()); // id → AbortController
  const timersRef = useRef(new Map<string, unknown>()); // id → 等待态计时句柄
  const disposedRef = useRef(false);
  const idSeqRef = useRef(0);
  const sessionRef = useRef<string>(opts.sessionId ?? "");
  sessionRef.current = opts.sessionId ?? "";

  const commitItems = useCallback((next: readonly AttachmentItem[]) => {
    itemsRef.current = next;
    setItemsState(next);
  }, []);
  const commitWaitIds = useCallback((next: readonly string[]) => {
    waitIdsRef.current = next;
    setWaitIdsState(next);
  }, []);
  const setSubmitting = useCallback((v: boolean) => {
    submittingRef.current = v;
    setSubmittingState(v);
  }, []);

  const nextId = useCallback(
    () => (mintId ? mintId() : `att-${(idSeqRef.current += 1)}`),
    [mintId],
  );
  const getSessionIdRef = useRef(opts.getSessionId);
  getSessionIdRef.current = opts.getSessionId;
  const getSessionId = useCallback(
    () => (typeof getSessionIdRef.current === "function" ? getSessionIdRef.current() : sessionRef.current),
    [],
  );

  function setItem(id: string, patch: Partial<AttachmentItem>): void {
    commitItems(Object.freeze(itemsRef.current.map((c) => (c.id === id ? Object.freeze({ ...c, ...patch }) : c))));
  }

  function startWait(id: string): void {
    stopWait(id);
    const h = schedule(() => {
      timersRef.current.delete(id);
      if (!disposedRef.current && itemsRef.current.some((c) => c.id === id && c.status === "uploading")) {
        commitWaitIds(Object.freeze([...waitIdsRef.current, id]));
      }
    }, waitDelayMs);
    timersRef.current.set(id, h);
  }
  function stopWait(id: string): void {
    const h = timersRef.current.get(id);
    if (h !== undefined) {
      unschedule(h);
      timersRef.current.delete(id);
    }
    if (waitIdsRef.current.includes(id)) commitWaitIds(Object.freeze(waitIdsRef.current.filter((x) => x !== id)));
  }

  /** 加入附件（上限/超限 ⇒ 显式提示，禁静默；旧面 addDraftFiles 语义迁移） */
  const addFiles = useCallback(
    (fileList: ReadonlyArray<AttachmentFileLike>) => {
      const { items: next, rejected } = addFilesPure(itemsRef.current, fileList, { mintId: nextId });
      commitItems(next);
      for (const r of rejected) {
        const reason =
          r.reason === "max" ? `草稿附件最多 5 个，「${r.name}」未加入` : `「${r.name}」超过 20MB 上限，未加入`;
        notice(reason, "warning");
      }
    },
    [commitItems, nextId, notice],
  );

  /** 删除一条（在途 ⇒ abort；等待态计时随条目一并撤） */
  const removeAttachment = useCallback(
    (id: string, { abortInflight = true }: { abortInflight?: boolean } = {}) => {
      stopWait(id);
      if (abortInflight) {
        const ctrl = controllersRef.current.get(id);
        if (ctrl) {
          try {
            ctrl.abort();
          } catch {
            /* noop */
          }
          controllersRef.current.delete(id);
        }
      }
      commitItems(removeItem(itemsRef.current, id));
    },
    [commitItems],
  );

  const moveAttachment = useCallback(
    (id: string, delta: number) => {
      commitItems(moveItem(itemsRef.current, id, delta));
    },
    [commitItems],
  );

  /** 失败重试（旧面 retryUpload 语义：复位 pending，不自动重发） */
  const retryUpload = useCallback((id: string) => {
    if (itemsRef.current.some((c) => c.id === id && c.status === "error")) setItem(id, { status: "pending", error: null });
  }, []);

  const abortAll = useCallback(() => {
    for (const [, ctrl] of controllersRef.current) {
      try {
        ctrl.abort();
      } catch {
        /* noop */
      }
    }
    controllersRef.current.clear();
  }, []);

  const clearTimers = useCallback(() => {
    for (const [, h] of timersRef.current) unschedule(h);
    timersRef.current.clear();
    commitWaitIds(Object.freeze([]));
  }, [commitWaitIds, unschedule]);

  /** 大文件分节等待（旧面 pollLargeFileStatus 语义迁移：1s×30；abort ⇒ 提前结束） */
  async function waitLargeReady(fileId: string, signal: AbortSignal): Promise<boolean> {
    for (let i = 0; i < pollMaxRounds; i += 1) {
      if (signal && signal.aborted) return false;
      await sleep(1000);
      if (signal && signal.aborted) return false;
      const r = pollApi ? await pollApi({ fileId, signal }) : { ok: false as const };
      if (r && r.ok) {
        const st = r.data || {};
        if (st.status === "ready" || (st.sections && st.finished_sections !== undefined && st.finished_sections >= st.sections)) return true;
      }
    }
    return false;
  }

  async function uploadOne(item: AttachmentItem, sessionId: string): Promise<{ ok: boolean; aborted?: boolean; id: string }> {
    const ctrl = new AbortController();
    controllersRef.current.set(item.id, ctrl);
    startWait(item.id);
    setItem(item.id, { status: "uploading" });
    try {
      if (typeof uploadApi !== "function") {
        // 缺省＝接线位未接：**显式失败**（禁静默假成功；宿主经 uploadDeps.uploadApi 注入真通道）
        throw new Error("上传通道未接线（宿主注入 uploadDeps.uploadApi）");
      }
      const r = await uploadApi(item.file, { sessionId, signal: ctrl.signal });
      if (!r || r.ok !== true) throw new Error((r && (r as { errorText?: string; errorCode?: string }).errorText) || (r as { errorCode?: string } | undefined)?.errorCode || "上传失败");
      const fileId = r.data && r.data.fileId;
      if (!fileId) throw new Error("上传回执缺 file_id");
      if (r.data && r.data.channel === "upload-large") {
        const ready = await waitLargeReady(fileId, ctrl.signal);
        if (!ready && !ctrl.signal.aborted) throw new Error("大文件分节处理超时");
      }
      setItem(item.id, { status: "done", fileId });
      stopWait(item.id);
      controllersRef.current.delete(item.id);
      return { ok: true, id: item.id };
    } catch (e) {
      stopWait(item.id);
      controllersRef.current.delete(item.id);
      if (e && (e as { name?: string }).name === "AbortError") return { ok: false, aborted: true, id: item.id };
      setItem(item.id, { status: "error", error: (e as Error)?.message || "上传失败" });
      onError((e as Error)?.message ? (e as Error).message : String(e));
      return { ok: false, id: item.id };
    }
  }

  /**
   * **A 方案（20260920，承大王令）**：解析本次提交的会话归属——无会话 ⇒ 先建会话再传。
   * 建会话**只经注入面**（宿主 `ensureSession()`）；未注入或建不出 ⇒ 返回 `''` ⇒
   * 调用方照旧 `blocked:'no-session'`（**零隐式建会话**、不静默）。
   */
  async function resolveSessionId(): Promise<string> {
    const cur = getSessionId();
    if (cur) return cur;
    if (typeof ensureSessionId !== "function") return "";
    try {
      return (await ensureSessionId()) || "";
    } catch {
      return "";
    }
  }

  /**
   * 提交编排（B-21①③）：在途 ⇒ **整体忽略**；成功 ⇒ 回执 metas 交调用方上抛＋栏内 done 退休；
   * 失败 ⇒ 条目落 error 可重试、草稿不消费。sessionId＝**调用时刻**取值（归属正确性锚）。
   */
  async function submitUploads(): Promise<SubmitUploadsResult> {
    if (submittingRef.current) return { ignored: true };
    if (errorItems(itemsRef.current).length) return { blocked: "error-cards" };
    if (!pendingItems(itemsRef.current).length) {
      const metas = doneMetas(itemsRef.current);
      const sessionId = await resolveSessionId(); // A 方案：无会话 ⇒ 先建会话
      retireDone(); // 全 done 提交（上轮失败残留的已传件）：上抛即退休，与主路径同语义
      return { ok: true, metas, sessionId };
    }
    const prevId = getSessionId(); // 提交前指针（切会话判定基准）
    const sessionId = await resolveSessionId(); // A 方案：无会话 ⇒ 先建会话再传
    if (!sessionId) return { blocked: "no-session" };
    setSubmitting(true);
    const pendings = pendingItems(itemsRef.current);
    const results = await Promise.all(pendings.map((c) => uploadOne(c, sessionId)));
    setSubmitting(false);
    // B-21③：切会话 ⇒ 本次提交作废（abort 已由会话指针效应触发）。
    // 🔴 A 方案避让：`prevId === ''` 且提交后指针**未离开**本次归属（含替身恒空态）
    //   ⇒ 本次会话是**本次提交自建**的（非用户切走），不算切会话。
    const nowId = getSessionId();
    const selfCreated = prevId === "" && (nowId === "" || nowId === sessionId);
    if (disposedRef.current || (nowId !== sessionId && !selfCreated)) return { ignored: true };
    if (results.some((r) => !r.ok)) {
      return { blocked: "upload-failed" }; // 失败 ⇒ 附件留栏（error 态可重试），调用方不消费草稿
    }
    const byId = new Map(itemsRef.current.map((c) => [c.id, c]));
    const metas = Object.freeze(pendings.map((c) => metaOf({ ...(byId.get(c.id) as AttachmentItem), status: "done" })));
    retireDone();
    return { ok: true, metas, sessionId };
  }

  /** 提交发出后退休已传条目（旧面「1.5s 自动移除」的确定性替代：上抛即退休，失败路径不经此处） */
  function retireDone(): void {
    commitItems(Object.freeze(itemsRef.current.filter((c: { status: string }) => c.status !== "done")));
  }

  // B-21③：会话指针变化 ⇒ 在途全撤＋清空＋显式提示（附件归属跟随会话；跨会话不带走）
  const prevSessionRef = useRef<string>(opts.sessionId ?? "");
  useEffect(() => {
    const ov = prevSessionRef.current;
    const nv = opts.sessionId ?? "";
    prevSessionRef.current = nv;
    if (disposedRef.current) return;
    if (ov && nv !== ov) {
      const flying = itemsRef.current.filter((c: { status: string }) => c.status === "uploading").length;
      abortAll();
      clearTimers();
      setSubmitting(false);
      commitItems(Object.freeze([]));
      if (flying) notice(`已切换会话，取消 ${flying} 个上传中的附件（附件归属跟随会话）`, "info");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts.sessionId]);

  const dispose = useCallback(() => {
    if (disposedRef.current) return;
    disposedRef.current = true;
    abortAll();
    clearTimers();
  }, [abortAll, clearTimers]);

  useEffect(() => () => dispose(), [dispose]);

  return {
    items,
    submitting,
    waitIds,
    uploadWaitVisible: waitIds.length > 0,
    hasPending: items.some((c) => c.status === "pending"),
    hasError: errorItems(items).length > 0,
    barEmpty: items.length === 0,
    addFiles,
    removeAttachment,
    moveAttachment,
    retryUpload,
    submitUploads,
    notice,
    abortAll,
    dispose,
  };
}

export default useComposerUpload;
